import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const NODES = [{
  label: 'Design',
  angle: 270
}, {
  label: 'Test',
  angle: 330
}, {
  label: 'Analyze',
  angle: 30
}, {
  label: 'Optimize',
  angle: 90
}, {
  label: 'Deploy',
  angle: 150
}, {
  label: 'Iterate',
  angle: 210
}];
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = (angleDeg - 90) * Math.PI / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad)
  };
}
export function GameLoopVisualization() {
  const [mounted, setMounted] = useState(false);
  const cx = 200;
  const cy = 200;
  const radius = 140;
  const nodeRadius = 28;
  useEffect(() => {
    setMounted(true);
  }, []);
  const nodePositions = NODES.map(node => ({
    ...node,
    ...polarToCartesian(cx, cy, radius, node.angle)
  }));
  return <motion.div className="relative w-full max-w-[400px] aspect-square" initial={{
    opacity: 0,
    scale: 0.8
  }} animate={mounted ? {
    opacity: 1,
    scale: 1
  } : {}} transition={{
    duration: 1.2,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-19">
      <svg viewBox="0 0 400 400" className="w-full h-full" aria-label="Animated game loop visualization showing the cycle: Design, Test, Analyze, Optimize, Deploy, Iterate" role="img" data-id="element-20">
        <defs data-id="element-21">
          <filter id="amber-glow" data-id="element-22">
            <feGaussianBlur stdDeviation="6" result="blur" data-id="element-23" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-24" />
          </filter>
          <filter id="ambient-glow" data-id="element-25">
            <feGaussianBlur stdDeviation="12" result="blur" data-id="element-26" />
            <feMerge data-id="element-27">
              <feMergeNode in="blur" data-id="element-28" />
              <feMergeNode in="SourceGraphic" data-id="element-29" />
            </feMerge>
          </filter>
          <linearGradient id="connection-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-30">
            <stop offset="0%" stopColor="#F5A623" stopOpacity="0.1" data-id="element-31" />
            <stop offset="50%" stopColor="#F5A623" stopOpacity="0.6" data-id="element-32" />
            <stop offset="100%" stopColor="#F5A623" stopOpacity="0.1" data-id="element-33" />
          </linearGradient>
          <radialGradient id="center-glow" cx="50%" cy="50%" r="50%" data-id="element-34">
            <stop offset="0%" stopColor="#F5A623" stopOpacity="0.08" data-id="element-35" />
            <stop offset="100%" stopColor="#F5A623" stopOpacity="0" data-id="element-36" />
          </radialGradient>
        </defs>

        {/* Center ambient glow */}
        <circle cx={cx} cy={cy} r={100} fill="url(#center-glow)" data-id="element-37" />

        {/* Outer ring - subtle */}
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#F5A623" strokeOpacity="0.08" strokeWidth="1" data-id="element-38" />

        {/* Connection lines between consecutive nodes */}
        {nodePositions.map((node, i) => {
        const next = nodePositions[(i + 1) % nodePositions.length];
        return <g key={`connection-${i}`} data-id="element-39">
              {/* Background line */}
              <line x1={node.x} y1={node.y} x2={next.x} y2={next.y} stroke="#F5A623" strokeOpacity="0.1" strokeWidth="1" data-id="element-40" />
              {/* Animated flowing line */}
              <line x1={node.x} y1={node.y} x2={next.x} y2={next.y} stroke="#F5A623" strokeOpacity="0.4" strokeWidth="1.5" strokeDasharray="4 12" strokeLinecap="round" data-id="element-41">
                <animate attributeName="stroke-dashoffset" from="0" to="-48" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" data-id="element-42" />
              </line>
            </g>;
      })}

        {/* Data stream particles along connections */}
        {nodePositions.map((node, i) => {
        const next = nodePositions[(i + 1) % nodePositions.length];
        return <circle key={`particle-${i}`} r="2" fill="#F5A623" opacity="0.7" data-id="element-43">
              <animateMotion dur={`${3 + i * 0.5}s`} repeatCount="indefinite" path={`M${node.x},${node.y} L${next.x},${next.y}`} data-id="element-44" />
              <animate attributeName="opacity" values="0;0.8;0" dur={`${3 + i * 0.5}s`} repeatCount="indefinite" data-id="element-45" />
            </circle>;
      })}

        {/* Nodes */}
        {nodePositions.map((node, i) => <g key={node.label} data-id="element-46">
            {/* Node pulse ring */}
            <circle cx={node.x} cy={node.y} r={nodeRadius + 8} fill="none" stroke="#F5A623" strokeWidth="1" data-id="element-47">
              <animate attributeName="r" values={`${nodeRadius + 4};${nodeRadius + 12};${nodeRadius + 4}`} dur={`${2.5 + i * 0.4}s`} repeatCount="indefinite" data-id="element-48" />
              <animate attributeName="opacity" values="0.3;0.05;0.3" dur={`${2.5 + i * 0.4}s`} repeatCount="indefinite" data-id="element-49" />
            </circle>

            {/* Node background */}
            <circle cx={node.x} cy={node.y} r={nodeRadius} fill="#0D1B2A" stroke="#F5A623" strokeWidth="1.5" strokeOpacity="0.5" filter="url(#amber-glow)" data-id="element-50" />

            {/* Node inner glow */}
            <circle cx={node.x} cy={node.y} r={nodeRadius - 2} fill="none" stroke="#F5A623" strokeWidth="0.5" strokeOpacity="0.2" data-id="element-51" />

            {/* Node label */}
            <text x={node.x} y={node.y} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="9" fontFamily="Sora, sans-serif" fontWeight="500" letterSpacing="0.5" data-id="element-52">
              {node.label}
            </text>
          </g>)}

        {/* Center keystone icon */}
        <g filter="url(#ambient-glow)" data-id="element-53">
          <polygon points={`${cx},${cy - 18} ${cx + 16},${cy + 8} ${cx},${cy + 2} ${cx - 16},${cy + 8}`} fill="none" stroke="#F5A623" strokeWidth="1.5" strokeOpacity="0.6" data-id="element-54">
            <animate attributeName="stroke-opacity" values="0.4;0.8;0.4" dur="3s" repeatCount="indefinite" data-id="element-55" />
          </polygon>
          <text x={cx} y={cy + 20} textAnchor="middle" fill="#F5A623" fontSize="7" fontFamily="Sora, sans-serif" fontWeight="600" letterSpacing="2" opacity="0.6" data-id="element-56">
            AI
          </text>
        </g>
      </svg>
    </motion.div>;
}